import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { App as CapApp } from '@capacitor/app';
import ChatList from './ChatList';
import ChatInterface from './ChatInterface';

const ChatLayout = () => {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle Android back button
  useEffect(() => {
    let capListener;
    
    // Try Capacitor first (APK)
    try {
      CapApp.addListener('backButton', ({ canGoBack }) => {
        if (chatId) {
          navigate('/chats');
        }
        // At chat list — don't exit
      }).then(l => { capListener = l; });
    } catch {}
    
    // Browser fallback
    window.history.pushState({ app: 1 }, '');
    window.history.pushState({ app: 2 }, '');
    const handlePopState = () => {
      if (chatId) navigate('/chats');
      window.history.pushState({ app: 1 }, '');
    };
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (capListener?.remove) capListener.remove();
    };
  }, [chatId, navigate]);

  // Callback to refresh chat list (called after AI response, etc.)
  const onChatUpdated = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  // Mobile view - show only one panel at a time
  if (isMobile) {
    if (chatId) {
      return <ChatInterface onChatUpdated={onChatUpdated} />;
    }
    return <ChatList />;
  }

  // Desktop view - split screen layout
  return (
    <div className={`h-screen flex ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
      {/* Left Panel - Chat List (30%) */}
      <div className={`w-[30%] min-w-[300px] max-w-[400px] border-r ${
        isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'
      } flex flex-col h-full overflow-hidden relative`}>
        <ChatList isEmbedded={true} selectedChatId={chatId} refreshKey={refreshKey} />
      </div>

      {/* Right Panel - Chat Interface (70%) */}
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        {chatId ? (
          <ChatInterface isEmbedded={true} onChatUpdated={onChatUpdated} />
        ) : (
          <div className={`flex-1 flex items-center justify-center ${
            isDarkMode ? 'bg-gray-800' : 'bg-[#F0EBE3]'
          }`}>
            <div className="text-center">
              <div className={`w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center ${
                isDarkMode ? 'bg-gray-700' : 'bg-white shadow-lg'
              }`}>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="1.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  className={`w-12 h-12 ${isDarkMode ? 'text-gray-500' : 'text-[#D4AF37]'}`}
                >
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <h2 className={`text-2xl font-heading mb-2 ${
                isDarkMode ? 'text-white' : 'text-gray-800'
              }`}>
                ChatBuzzz Web
              </h2>
              <p className={`text-sm max-w-md ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Select a chat from the left to start messaging.
                <br />
                Your messages are end-to-end encrypted.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatLayout;
